<template>
  <section
    id="about"
    class="relative md:py-24 bg-gradient-to-b from-slate-50 to-white dark:from-slate-800 dark:to-slate-900"
  >
    <div class="container relative">
      <div class="grid items-center grid-cols-1 gap-6 md:grid-cols-12">
        <!-- Image -->
        <div class="md:col-span-6">
          <div class="lg:me-8">
            <img
              src="../assets/images/home.jpg"
              class="object-cover rounded-full shadow dark:shadow-gray-700"
              style="height: 500px"
              alt="About"
            />
          </div>
        </div>

        <!-- Text -->
        <div class="md:col-span-6">
          <div class="lg:ms-8">
            <!-- Title -->
            <h6
              class="mb-2 text-sm font-semibold tracking-wider uppercase text-primary"
            >
              {{ t("about_tag") }}
            </h6>

            <!-- Subtitle -->
            <h3
              class="mb-4 text-2xl font-semibold leading-normal text-slate-900 dark:text-white"
            >
              {{ t("about_subtitle") }}
            </h3>

            <!-- Vision / Mission / Values -->
            <div class="mb-8 space-y-5">
              <!-- Vision -->
              <div class="flex items-start gap-3">
                <div
                  class="flex items-center justify-center rounded-full w-14 h-14 bg-secondary/40 text-primary shrink-0"
                >
                  <i class="text-2xl mdi mdi-eye-outline"></i>
                </div>
                <div>
                  <h5
                    class="text-base font-semibold text-slate-900 dark:text-white"
                  >
                    {{ t("vision_title") }}
                  </h5>
                  <p
                    class="text-sm leading-relaxed text-slate-600 dark:text-slate-400"
                  >
                    {{ t("vision_text") }}
                  </p>
                </div>
              </div>

              <!-- Mission -->
              <div class="flex items-start gap-3">
                <div
                  class="flex items-center justify-center rounded-full w-14 h-14 bg-secondary/40 text-primary shrink-0"
                >
                  <i class="text-2xl mdi mdi-bullhorn-outline"></i>
                </div>
                <div>
                  <h5
                    class="text-base font-semibold text-slate-900 dark:text-white"
                  >
                    {{ t("mission_title") }}
                  </h5>
                  <p
                    class="text-sm leading-relaxed text-slate-600 dark:text-slate-400"
                  >
                    {{ t("mission_text") }}
                  </p>
                </div>
              </div>

              <!-- Values -->
              <div class="flex items-start gap-3">
                <div
                  class="flex items-center justify-center rounded-full w-14 h-14 bg-secondary/40 text-primary shrink-0"
                >
                  <i class="text-2xl mdi mdi-target"></i>
                </div>
                <div>
                  <h5
                    class="text-base font-semibold text-slate-900 dark:text-white"
                  >
                    {{ t("values_title") }}
                  </h5>

                  <ul
                    class="mt-2 space-y-2 text-sm list-disc list-inside text-slate-600 dark:text-slate-400"
                  >
                    <li
                      v-for="(v, i) in valuesListObjs"
                      :key="'obj-' + i"
                      class="ms-4"
                    >
                      <span
                        class="font-semibold text-slate-900 dark:text-white"
                      >
                        {{ v.title }}
                      </span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <!-- Button -->
            <a
              href="/contact"
              class="inline-flex items-center justify-center h-10 px-6 font-medium tracking-wide text-white transition rounded-md bg-primary hover:bg-primary-light"
              :title="roleLabel || null"
            >
              <!-- optional role icon next to label -->
              <i v-if="roleIcon" :class="['mdi text-lg me-2', roleIcon]"></i>

              {{ t("contact_us") }}

              <i
                :class="[
                  'text-lg align-middle mdi',
                  lang === 'ar'
                    ? 'mdi-chevron-left ms-1'
                    : 'mdi-chevron-right me-1',
                ]"
              ></i>
            </a>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from "vue";
import { messages } from "@/i18n";
import { useRoleLabel } from "@/composables/useRoleLabel";

// ===== lang handling (same idea as before) =====
const normalizeLang = (raw) =>
  String(raw || "").startsWith("en") ? "en" : "ar";

const lang = ref(
  normalizeLang(
    document.documentElement.lang || localStorage.getItem("app_lang") || "ar"
  )
);

let observer = null;

// ===== cookie helper =====
function getCookie(name) {
  const match = document.cookie.match(
    new RegExp(
      "(?:^|; )" + name.replace(/([.$?*|{}()[\]\\/+^])/g, "\\$1") + "=([^;]*)"
    )
  );
  return match ? decodeURIComponent(match[1]) : null;
}

// ===== user role from cookie =====
const user = ref(null);

function loadUserFromCookie() {
  const raw = getCookie("auth_user");
  if (!raw) {
    user.value = null;
    return;
  }

  try {
    user.value = JSON.parse(raw);
  } catch (e) {
    user.value = null;
  }
}

const roleSource = computed(() => user.value?.role || user.value?.type || "");

// ✅ useRoleLabel (same mechanism as Topbar)
const roleLabel = useRoleLabel(roleSource, lang);

// ✅ icon per role label/source (best practice: based on roleSource not label)
const roleIcon = computed(() => {
  const r = String(roleSource.value || "").toLowerCase();

  // adapt your actual roles here
  if (r.includes("admin")) return "mdi-shield-account-outline";
  if (r.includes("office")) return "mdi-office-building-outline";
  if (r.includes("consultant")) return "mdi-account-tie-outline";
  if (r.includes("client")) return "mdi-account-outline";

  return "mdi-account-circle-outline";
});

// ===== translations =====
const t = (key) => messages?.[lang.value]?.[key] || key;

// ===== values list =====
const valuesListObjs = computed(() => {
  const raw = messages?.[lang.value]?.values_list;
  if (!Array.isArray(raw) || raw.length === 0) return [];

  if (typeof raw[0] === "string") {
    return raw.map((s) => ({ title: s, desc: "" }));
  }

  return raw
    .map((o) => ({
      title: o?.title || "",
      desc: o?.desc || "",
    }))
    .filter((x) => x.title);
});

// ===== lifecycle =====
onMounted(() => {
  loadUserFromCookie();

  observer = new MutationObserver((muts) => {
    for (const m of muts) {
      if (m.attributeName === "lang") {
        lang.value = normalizeLang(document.documentElement.lang || "ar");
      }
    }
  });

  observer.observe(document.documentElement, { attributes: true });
});

onBeforeUnmount(() => {
  if (observer) observer.disconnect();
});
</script>
